/** Telas de identidade visual: marca do escritório, foto de perfil e preferências de exibição. */
import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useBrand, type Brand, type LoginLook } from "@/lib/brand";
import { useLookups } from "@/lib/catalog";
import { colorAt, contrast, extractPalette, fitDataUrl, loadImage, pickImage } from "@/lib/image";
import { useCropper, type Crop, type CropResult } from "./cropper";
import { Avatar } from "./icons";
import { Card, ErrorBox, Field, FormGrid, Modal, useAct } from "./kit";
import { LOGIN_DEFAULTS, LoginFrame } from "./login-frame";
import { toast } from "./feedback";
import { getPrefs, setPrefs, type Prefs } from "./theme";

const LIMIT = 690_000;
function fromDataUrl(u: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => rej(new Error("imagem inválida")); i.src = u; });
}

// ------------------------------------------------------------ foto de perfil

export function AvatarEditor({ userId, name, admin }: { userId: string | undefined; name: string; admin?: boolean }): JSX.Element {
  const base = admin ? `/config/usuarios/${userId}/avatar` : "/conta/avatar";
  const { catalog } = useLookups();
  const has = !!catalog?.users.find((u) => u.id === userId)?.avatarUpdatedAt;
  const qc = useQueryClient();
  const crop = useCropper();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const opts = { aspect: 1, round: true, title: "Ajustar foto" };
  const send = async (r: CropResult, withSource: boolean) => {
    setBusy(true);
    try {
      await api.put(base, { dataUrl: r.render(256, 256, "image/jpeg", "#ffffff", 0.86), crop: r.crop, ...(withSource ? { source: r.source } : {}) });
      await qc.invalidateQueries({ queryKey: ["catalogo"] });
      toast.ok("Foto atualizada");
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  const upload = async () => {
    const f = await pickImage(); if (!f) return;
    setErr(null);
    try { const r = await crop.open(f, opts); if (r) await send(r, true); } catch (e) { setErr(e); }
  };
  const adjust = async () => {
    setErr(null);
    try {
      const o = await api.get<{ src: string | null; crop: Crop | null }>(`${base}/original`);
      if (!o.src) { setErr(new Error("Esta foto foi enviada antes do ajuste existir: envie de novo uma vez (Trocar foto).")); return; }
      const r = await crop.open(o.src, { ...opts, initial: o.crop });
      if (r) await send(r, false);
    } catch (e) { setErr(e); }
  };
  const remove = async () => {
    setBusy(true); setErr(null);
    try { await api.del(base); await qc.invalidateQueries({ queryKey: ["catalogo"] }); toast.ok("Foto removida"); } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <div className="row nowrap" style={{ gap: 14, alignItems: "center" }}>
      <Avatar name={name} id={userId} size={admin ? 56 : 88} />
      <div className="stack" style={{ gap: 6 }}>
        <div className="row">
          {has && <button className="btn small" disabled={busy} onClick={() => void adjust()}>Ajustar</button>}
          <button className="btn small" disabled={busy || !userId} onClick={() => void upload()}>{has ? "Trocar foto" : "Enviar foto"}</button>
          {has && <button className="btn small" disabled={busy} onClick={() => void remove()}>Remover</button>}
        </div>
        <div className="muted small">JPG ou PNG. Você escolhe o enquadramento e o zoom. Aparece no topo, nas atividades e na agenda.</div>
        <ErrorBox error={err} />
      </div>
      {crop.el}
    </div>
  );
}

// ------------------------------------------------------------ preferências da pessoa

export function DisplayPrefsCard(): JSX.Element {
  const [p, setP] = useState<Prefs>(getPrefs());
  const up = (x: Partial<Prefs>) => setP(setPrefs(x));
  const opt = <K extends keyof Prefs>(k: K, v: Prefs[K], label: ReactNode) => (
    <button key={String(v)} className={`btn small ${p[k] === v ? "primary" : ""}`} onClick={() => up({ [k]: v } as Partial<Prefs>)}>{label}</button>
  );
  return (
    <Card title="Exibição (só para você, neste navegador)">
      <div className="stack">
        <Field label="Tema"><div className="row">{opt("mode", "claro", "Claro")}{opt("mode", "escuro", "Escuro")}{opt("mode", "auto", "Automático (segue o computador)")}</div></Field>
        <Field label="Tamanho do texto"><div className="row">{opt("size", "compacto", "Compacto")}{opt("size", "normal", "Normal")}{opt("size", "grande", "Grande")}</div></Field>
        <Field label="Linhas das tabelas"><div className="row">{opt("density", "compacta", "Compactas")}{opt("density", "normal", "Normais")}{opt("density", "confortavel", "Espaçadas")}</div></Field>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------ marca do escritório

function ImageSlot({ label, hint, value, square, onAdjust, onPick, onClear }: { label: string; hint?: string; value?: string | null; square?: boolean; onAdjust?: () => void; onPick: () => void; onClear: () => void }): JSX.Element {
  return (
    <div className="logo-slot">
      <div className={`prev light ${square ? "square" : ""}`}>{value ? <img src={value} alt={label} /> : <span className="muted small">sem imagem</span>}</div>
      <div className="stack grow" style={{ gap: 4 }}>
        <b className="small">{label}</b>
        {hint && <span className="muted small">{hint}</span>}
        <div className="row">
          {value && onAdjust && <button className="btn small" onClick={onAdjust}>Ajustar</button>}
          <button className="btn small" onClick={onPick}>{value ? "Trocar" : "Enviar"}</button>
          {value && <button className="link-btn small" onClick={onClear}>remover</button>}
        </div>
      </div>
    </div>
  );
}

type Sources = Record<string, { src: string; crop: Crop }>;

export function BrandSettings(): JSX.Element {
  const { data: current } = useBrand();
  const { data: savedSrc } = useQuery({ queryKey: ["marca-originais"], queryFn: () => api.get<Sources>("/config/marca/originais"), staleTime: 300_000 });
  const { catalog } = useLookups();
  const crop = useCropper();
  const [d, setD] = useState<Brand | null>(null);
  const [srcDraft, setSrcDraft] = useState<Sources | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const b: Brand = d ?? current ?? {};
  const sources: Sources = srcDraft ?? savedSrc ?? {};
  const up = (p: Partial<Brand>) => setD({ ...b, ...p });
  const save = useAct(async () => {
    await api.put("/config/marca", b);
    if (srcDraft) await api.put("/config/marca/originais", srcDraft);
  }, [["marca"], ["marca-originais"]], () => { setD(null); setSrcDraft(null); }, "Identidade visual salva");

  type O = { aspect: number; title: string; hint?: string; start?: "cover" | "fit" };
  /** Enviar (novo arquivo) ou ajustar (original guardado) uma imagem; devolve o resultado e guarda o original. */
  const edit = async (key: string, o: O, adjust: boolean): Promise<CropResult | null> => {
    setErr(null);
    try {
      let r: CropResult | null;
      if (adjust) {
        const s0 = sources[key];
        if (!s0) { setErr(new Error("Esta imagem foi enviada antes do ajuste existir: use Trocar e envie de novo uma vez.")); return null; }
        r = await crop.open(s0.src, { ...o, initial: s0.crop });
      } else {
        const f = await pickImage(); if (!f) return null;
        r = await crop.open(f, o);
      }
      if (r) setSrcDraft({ ...sources, [key]: { src: r.source, crop: r.crop } });
      return r;
    } catch (e) { setErr(e); return null; }
  };
  const dropSource = (key: string) => { const n = { ...sources }; delete n[key]; setSrcDraft(n); };
  const report = async (src: string | null | undefined): Promise<string | null> => {
    if (!src) return null;
    return fitDataUrl(await fromDataUrl(src), 420, 140, "image/jpeg", "#ffffff", 0.9);
  };
  const iconOpts: O = { aspect: 1, title: "Ajustar ícone do escritório", hint: "Fica no quadrado do menu, na aba do navegador e no celular." };
  const setIcon = async (adjust: boolean) => {
    const r = await edit("icone", iconOpts, adjust); if (!r) return;
    up({ favicon: r.render(64, 64), icon192: r.render(192, 192, "image/png", "#ffffff"), icon512: r.render(512, 512, "image/png", "#ffffff") });
  };
  const setWide = async (adjust: boolean) => {
    const r = await edit("logo", { aspect: 3, start: "fit", title: "Ajustar logo horizontal", hint: "Áreas quadriculadas ficam transparentes." }, adjust);
    if (!r) return;
    const u = r.render(600, 200);
    if (u.length > LIMIT) { setErr(new Error("Logo pesada demais. Use um PNG mais simples.")); return; }
    setD({ ...b, logoDark: u, logoLight: null, reportJpeg: await report(u) });
  };
  const setBgLogo = async (adjust: boolean) => {
    const r = await edit("logofundo", { aspect: 3, start: "fit", title: "Ajustar logo do fundo do login", hint: "Use a versão clara da logo (para fundo escuro). Áreas quadriculadas ficam transparentes." }, adjust);
    if (!r) return;
    const u = r.render(900, 300);
    if (u.length > LIMIT) { setErr(new Error("Logo pesada demais. Use um PNG mais simples.")); return; }
    up({ loginLogo: u });
  };
  const units = (catalog?.units ?? []).filter((u) => u.active);
  const setUnitIcon = async (id: string, mode: "novo" | "ajustar" | "remover") => {
    let v: string | null = null;
    if (mode !== "remover") {
      const r = await edit(`unidade:${id}`, { aspect: 1, title: "Ajustar ícone da unidade" }, mode === "ajustar");
      if (!r) return;
      v = r.render(192, 192, "image/png", "#ffffff");
    } else dropSource(`unidade:${id}`);
    const cur = { ...(b.units ?? {}) };
    const n = { ...(cur[id] ?? {}), icon: v };
    if (!n.icon && !n.logoLight && !n.logoDark) delete cur[id]; else cur[id] = n;
    up({ units: cur });
  };
  const foot = b.footer ?? {};
  const wide = b.logoDark ?? b.logoLight;

  return (
    <div className="stack">
      <div className="grid cols-2">
        <Card title="Ícone do escritório">
          <div className="stack">
            <ImageSlot label="Ícone (quadrado)" square value={b.icon192 ?? b.favicon}
              hint="Fica no quadrado do topo do menu, na aba do navegador e no atalho do celular."
              onAdjust={() => void setIcon(true)} onPick={() => void setIcon(false)}
              onClear={() => { up({ favicon: null, icon192: null, icon512: null }); dropSource("icone"); }} />
            {units.length > 1 && <>
              <div className="muted small" style={{ marginTop: 6 }}>Ícone por unidade (opcional). Quando a unidade escolhida no menu (ou a da pessoa) tem ícone próprio, ele aparece no lugar do ícone do escritório.</div>
              {units.map((u) => (
                <ImageSlot key={u.id} label={u.name} square value={b.units?.[u.id]?.icon}
                  onAdjust={() => void setUnitIcon(u.id, "ajustar")} onPick={() => void setUnitIcon(u.id, "novo")} onClear={() => void setUnitIcon(u.id, "remover")} />
              ))}
            </>}
          </div>
        </Card>
        <Card title="Tela de login, e-mails e PDF" className="grid-span-2">
          <div className="stack">
            <LoginDesigner b={b} up={up} wide={wide} onErr={setErr}
              onWide={(adjust) => void setWide(adjust)} onWideClear={() => { setD({ ...b, logoDark: null, logoLight: null, reportJpeg: null }); dropSource("logo"); }}
              onBgLogo={(adjust) => void setBgLogo(adjust)} onBgLogoClear={() => { up({ loginLogo: null }); dropSource("logofundo"); }}
              hasBgSource={!!sources.logofundo} hasWideSource={!!sources.logo} />
            <div className="muted small">Rodapé dos e-mails enviados pelo sistema:</div>
            <FormGrid cols={2}>
              <Field label="Endereço" span={2}><input className="input" maxLength={200} value={foot.address ?? ""} onChange={(e) => up({ footer: { ...foot, address: e.target.value || null } })} /></Field>
              <Field label="Telefone"><input className="input" maxLength={60} value={foot.phone ?? ""} onChange={(e) => up({ footer: { ...foot, phone: e.target.value || null } })} /></Field>
              <Field label="E-mail"><input className="input" maxLength={120} value={foot.email ?? ""} onChange={(e) => up({ footer: { ...foot, email: e.target.value || null } })} /></Field>
              <Field label="Site" span={2}><input className="input" maxLength={120} value={foot.site ?? ""} onChange={(e) => up({ footer: { ...foot, site: e.target.value || null } })} placeholder="escritoriovg.adv.br" /></Field>
            </FormGrid>
          </div>
        </Card>
      </div>
      <div className="row">
        <button className="btn primary" disabled={(!d && !srcDraft) || save.isPending} onClick={() => save.mutate()}>Salvar identidade visual</button>
        {(d || srcDraft) && <button className="btn" onClick={() => { setD(null); setSrcDraft(null); }}>Descartar</button>}
        <span className="muted small">Planilhas Excel continuam sem logo; os PDFs saem com ela.</span>
      </div>
      <ErrorBox error={err ?? save.error} />
      {crop.el}
    </div>
  );
}

// ------------------------------------------------------------ tela de login (designer)

interface DesignerProps {
  b: Brand; up: (p: Partial<Brand>) => void; wide: string | null | undefined; onErr: (e: unknown) => void;
  onWide: (adjust: boolean) => void; onWideClear: () => void; onBgLogo: (adjust: boolean) => void; onBgLogoClear: () => void;
  hasWideSource: boolean; hasBgSource: boolean;
}

function LoginDesigner({ b, up, wide, onErr, onWide, onWideClear, onBgLogo, onBgLogoClear, hasWideSource, hasBgSource }: DesignerProps): JSX.Element {
  const L: LoginLook = b.login ?? {};
  const set = (p: Partial<LoginLook>) => up({ login: { ...L, ...p } });
  const bgLogo = b.loginLogo ?? b.logoLight ?? wide ?? null;
  const bgImage = ((b.login?.bgMode === "mosaico" ? null : b.loginBgImage) ?? b.loginLogo ?? wide ?? b.logoLight) || null;
  const mode = L.bgMode ?? "cor";
  /** Imagem do fundo: até 2400 px; foto vira JPEG; PNG mantém a transparência. Ao usar, já sugere a cor da borda para completar o fundo. */
  const pickBg = async () => {
    try {
      const f = await pickImage(); if (!f) return;
      const img = await loadImage(f);
      const png = f.type === "image/png" || f.type === "image/svg+xml";
      let u = fitDataUrl(img, 2400, 1600, png ? "image/png" : "image/jpeg", png ? undefined : "#000000", 0.85);
      if (u.length > 2_500_000) u = fitDataUrl(img, 1800, 1200, "image/jpeg", "#000000", 0.8);
      if (u.length > 2_500_000) { onErr(new Error("Imagem grande demais. Use uma imagem menor (até ~2 MB).")); return; }
      const edge = await colorAt(u, 0.01, 0.5);
      up({ loginBgImage: u, ...(edge ? { loginBg: edge } : {}), login: { ...L, bgMode: L.bgMode === "dividida" ? "dividida" : "imagem" } });
    } catch (e) { onErr(e); }
  };
  const useLogoAsBg = async () => {
    const src = b.loginLogo ?? wide ?? b.logoLight;
    const edge = src ? await colorAt(src, 0.01, 0.5).catch(() => null) : null;
    up({ loginBgImage: null, ...(edge ? { loginBg: edge } : {}), login: { ...L, bgMode: L.bgMode === "dividida" ? "dividida" : "imagem", bgFit: L.bgFit ?? "inteira" } });
  };
  const [palette, setPalette] = useState<string[]>([]);
  const [picker, setPicker] = useState<null | ((c: string) => void)>(null);
  useEffect(() => {
    let alive = true;
    const srcs = [wide, b.loginLogo].filter((x): x is string => !!x);
    Promise.all(srcs.map((u) => extractPalette(u, 6).catch(() => [] as string[])))
      .then((ls) => { if (alive) setPalette([...new Set(ls.flat())].slice(0, 10)); });
    return () => { alive = false; };
  }, [wide, b.loginLogo]);
  const bg = b.loginBg ?? LOGIN_DEFAULTS.bg;
  const card = L.card ?? LOGIN_DEFAULTS.card, text = L.text ?? LOGIN_DEFAULTS.text;
  const btn = L.button ?? "#1F3350", btnText = L.buttonText ?? LOGIN_DEFAULTS.buttonText;
  const warn = [
    contrast(card, text) < 4.5 && "O texto do cartão está com pouco contraste com o fundo do cartão.",
    contrast(btn, btnText) < 4.5 && "O texto do botão está com pouco contraste com a cor do botão.",
  ].filter(Boolean) as string[];
  const cf = (label: string, value: string, onChange: (v: string | null) => void, isDefault: boolean) => (
    <Field label={label}><ColorField value={value} onChange={onChange} isDefault={isDefault} palette={palette} onPickLogo={(cb) => setPicker(() => cb)} /></Field>
  );
  return (
    <>
      <LoginFrame preview bg={b.loginBg} look={L} bgLogoUrl={bgLogo} cardLogoUrl={wide} bgImageUrl={bgImage} officeName="Seu escritório" note={b.loginText}>
        <div className="lp-fake"><div className="lp-l" /><div className="lp-l" /><div className="lp-b">Entrar</div></div>
      </LoginFrame>
      {warn.map((w) => <div key={w} className="warn-box small">{w}</div>)}

      <div className="stack gap-6">
        <b className="small">Logo horizontal (cartão do login, e-mails e PDF)</b>
        <div className="row wrap">
          {wide && <button className="btn small" onClick={() => onWide(true)} disabled={!hasWideSource} title={hasWideSource ? "" : "Envie de novo uma vez para poder ajustar"}>Ajustar</button>}
          <button className="btn small" onClick={() => onWide(false)}>{wide ? "Trocar" : "Enviar"}</button>
          {wide && <button className="link-btn small" onClick={onWideClear}>remover</button>}
        </div>
        <b className="small">Logo extra sobre o fundo (opcional)</b>
        <div className="row wrap">
          {b.loginLogo && <button className="btn small" onClick={() => onBgLogo(true)} disabled={!hasBgSource}>Ajustar</button>}
          <button className="btn small" onClick={() => onBgLogo(false)}>{b.loginLogo ? "Trocar" : "Enviar outra"}</button>
          {b.loginLogo && <button className="link-btn small" onClick={onBgLogoClear}>usar a horizontal</button>}
        </div>
        <div className="muted small">Sem logo própria para o fundo, usa a logo horizontal. Para fundo escuro, o ideal é a versão clara e transparente da logo.</div>
      </div>

      <div className="stack gap-6">
        <b className="small">Fundo da tela</b>
        <div className="seg">
          <button className={mode === "cor" ? "on" : ""} onClick={() => set({ bgMode: "cor" })}>Cor</button>
          <button className={mode === "mosaico" ? "on" : ""} onClick={() => set({ bgMode: "mosaico" })}>Mosaico da logo</button>
          <button className={mode === "dividida" ? "on" : ""} onClick={() => set({ bgMode: "dividida", bgFit: L.bgFit ?? "inteira" })}>Tela dividida</button>
          <button className={mode === "imagem" ? "on" : ""} onClick={() => set({ bgMode: "imagem" })}>Imagem de fundo</button>
        </div>
        {mode === "mosaico" && (
          <>
            <FormGrid cols={2}>
              <Field label={`Tamanho de cada logo (${L.tileSize ?? 140}px)`}><input type="range" min={30} max={400} step={5} value={L.tileSize ?? 140} onChange={(e) => set({ tileSize: Number(e.target.value) })} /></Field>
              <Field label={`Espaço entre as logos (${L.tileGap ?? 60}px)`}><input type="range" min={0} max={300} step={5} value={L.tileGap ?? 60} onChange={(e) => set({ tileGap: Number(e.target.value) })} /></Field>
              <Field label={`Intensidade das logos (${L.tileOpacity ?? 35}%)`}><input type="range" min={5} max={100} step={5} value={L.tileOpacity ?? 35} onChange={(e) => set({ tileOpacity: Number(e.target.value) })} /></Field>
              <Field label="Arrumação">
                <select className="input" value={L.tilePattern ?? "intercalado"} onChange={(e) => set({ tilePattern: e.target.value as LoginLook["tilePattern"] })}>
                  <option value="intercalado">Intercalada (como tijolos)</option><option value="alinhado">Alinhada (grade)</option>
                </select>
              </Field>
            </FormGrid>
            <div className="muted small">A logo se repete em pequenos quadros cobrindo a tela; a cor do fundo fica por baixo. Usa a logo do fundo (ou a horizontal). Dica: intensidade baixa (20–40%) deixa o cartão em destaque.</div>
          </>
        )}
        {mode === "dividida" && (
          <>
            <FormGrid cols={2}>
              <Field label="Lado da imagem"><select className="input" value={L.splitSide ?? "esquerda"} onChange={(e) => set({ splitSide: e.target.value as LoginLook["splitSide"] })}><option value="esquerda">Esquerda</option><option value="direita">Direita</option></select></Field>
              <Field label="Como a imagem ocupa a metade">
                <select className="input" value={L.bgFit ?? "inteira"} onChange={(e) => set({ bgFit: e.target.value as LoginLook["bgFit"] })}><option value="inteira">Imagem inteira (sobras com a cor do fundo)</option><option value="cobrir">Cobrir a metade toda</option></select>
              </Field>
            </FormGrid>
            <div className="row wrap">
              <button className="btn small" onClick={() => void useLogoAsBg()}>Usar a logo</button>
              <button className="btn small" onClick={() => void pickBg()}>{b.loginBgImage ? "Trocar imagem" : "Enviar outra imagem"}</button>
              {b.loginBgImage && <button className="link-btn small" onClick={() => up({ loginBgImage: null })}>voltar para a logo</button>}
            </div>
            <div className="muted small">Metade da tela com a sua logo/imagem e a outra metade com o login (na cor do cartão). No celular, a imagem fica em cima.</div>
          </>
        )}
        {mode === "imagem" && (
          <>
            <div className="row wrap">
              <button className="btn small" onClick={() => void useLogoAsBg()}>Usar a logo como fundo</button>
              <button className="btn small" onClick={() => void pickBg()}>{b.loginBgImage ? "Trocar imagem" : "Enviar outra imagem"}</button>
              {b.loginBgImage && <button className="link-btn small" onClick={() => up({ loginBgImage: null })}>voltar para a logo</button>}
            </div>
            <FormGrid cols={2}>
              <Field label="Como a imagem ocupa o fundo">
                <select className="input" value={L.bgFit ?? "cobrir"} onChange={(e) => set({ bgFit: e.target.value as LoginLook["bgFit"] })}>
                  <option value="cobrir">Cobrir a tela toda (pode cortar as bordas)</option>
                  <option value="inteira">Imagem inteira (o resto com a cor do fundo)</option>
                </select>
              </Field>
              <Field label={`Escurecer a imagem (${L.bgOverlay ?? 0}%)`}>
                <input type="range" min={0} max={80} step={5} value={L.bgOverlay ?? 0} onChange={(e) => set({ bgOverlay: Number(e.target.value) })} />
              </Field>
            </FormGrid>
            <div className="muted small">“Imagem inteira” completa as sobras com a cor do fundo — já sugerimos a cor da borda da imagem, para emendar sem aparecer o recorte. Para cobrir a tela sem perder nitidez, prefira uma imagem grande (1920 px ou mais).</div>
          </>
        )}
      </div>

      <FormGrid cols={3}>
        <Field label="Logo extra sobre o fundo">
          <select className="input" value={L.bgLogo ?? "nenhuma"} onChange={(e) => set({ bgLogo: e.target.value as LoginLook["bgLogo"] })}>
            <option value="nenhuma">Não mostrar</option><option value="acima">Acima do cartão</option><option value="lado">Ao lado do cartão</option>
          </select>
        </Field>
        <Field label={`Tamanho da logo do fundo (${L.bgLogoSize ?? LOGIN_DEFAULTS.bgLogoSize}px)`}>
          <input type="range" min={40} max={360} step={4} value={L.bgLogoSize ?? LOGIN_DEFAULTS.bgLogoSize} onChange={(e) => set({ bgLogoSize: Number(e.target.value) })} disabled={(L.bgLogo ?? "nenhuma") === "nenhuma"} />
        </Field>
        <Field label={`Tamanho da logo no cartão (${L.cardLogoSize ?? LOGIN_DEFAULTS.cardLogoSize}px)`}>
          <input type="range" min={24} max={160} step={2} value={L.cardLogoSize ?? LOGIN_DEFAULTS.cardLogoSize} onChange={(e) => set({ cardLogoSize: Number(e.target.value) })} disabled={L.cardLogo === false} />
        </Field>
      </FormGrid>
      <div className="row wrap">
        <label className="check"><input type="checkbox" checked={L.cardLogo ?? true} onChange={(e) => set({ cardLogo: e.target.checked })} /> Logo dentro do cartão</label>
        <label className="check"><input type="checkbox" checked={L.showName ?? true} onChange={(e) => set({ showName: e.target.checked })} /> Nome do escritório no cartão</label>
      </div>

      <FormGrid cols={2}>
        {cf(mode === "cor" ? "Fundo (cor 1)" : "Cor do fundo (por baixo da imagem)", bg, (v) => up({ loginBg: v }), !b.loginBg)}
        {cf("Fundo (cor 2 — degradê, opcional)", L.bg2 ?? bg, (v) => set({ bg2: v }), !L.bg2)}
        {cf("Cartão", card, (v) => set({ card: v }), !L.card)}
        {cf("Texto do cartão", text, (v) => set({ text: v }), !L.text)}
        {cf("Botão “Entrar”", btn, (v) => set({ button: v }), !L.button)}
        {cf("Texto do botão", btnText, (v) => set({ buttonText: v }), !L.buttonText)}
      </FormGrid>
      {palette.length > 0 && <div className="muted small">As amostras são as cores principais da sua logo. O conta-gotas (💧) pega a cor de qualquer ponto da logo.</div>}
      <Field label="Frase abaixo do login (opcional)"><input className="input" maxLength={160} value={b.loginText ?? ""} onChange={(e) => up({ loginText: e.target.value || null })} placeholder="Ex.: Acesso restrito à equipe" /></Field>
      {picker && <PickFromLogo images={[wide, b.loginLogo].filter((x): x is string => !!x)} onClose={() => setPicker(null)} onPick={(c) => { picker(c); setPicker(null); }} />}
    </>
  );
}

function ColorField({ value, onChange, isDefault, palette, onPickLogo }: { value: string; onChange: (v: string | null) => void; isDefault: boolean; palette: string[]; onPickLogo: (cb: (c: string) => void) => void }): JSX.Element {
  const [txt, setTxt] = useState(value);
  useEffect(() => setTxt(value), [value]);
  const native = typeof window !== "undefined" && "EyeDropper" in window;
  const drop = async () => {
    try {
      const Ed = (window as unknown as { EyeDropper: new () => { open: () => Promise<{ sRGBHex: string }> } }).EyeDropper;
      const r = await new Ed().open();
      onChange(r.sRGBHex.toUpperCase());
    } catch { /* cancelado */ }
  };
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="color-field">
        <input type="color" className="input" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} />
        <input className="input hex" value={txt} onChange={(e) => { setTxt(e.target.value); if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onChange(e.target.value.toUpperCase()); }} />
        <button type="button" className="btn small" title="Pegar a cor de um ponto da logo" onClick={() => onPickLogo((c) => onChange(c))}>💧 da logo</button>
        {native && <button type="button" className="btn small ghost" title="Conta-gotas: pega a cor de qualquer ponto da tela" onClick={() => void drop()}>💧 da tela</button>}
        {!isDefault && <button type="button" className="link-btn small" onClick={() => onChange(null)}>padrão</button>}
      </div>
      {palette.length > 0 && <div className="swatches">{palette.map((c) => <span key={c} className="sw" title={c} style={{ background: c }} onClick={() => onChange(c)} />)}</div>}
    </div>
  );
}

function PickFromLogo({ images, onPick, onClose }: { images: string[]; onPick: (c: string) => void; onClose: () => void }): JSX.Element {
  const [hover, setHover] = useState<string | null>(null);
  const pos = (e: React.MouseEvent<HTMLImageElement>) => { const r = e.currentTarget.getBoundingClientRect(); return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height] as const; };
  return (
    <Modal title="Conta-gotas: clique na cor da logo" onClose={onClose} footer={<><span className="row nowrap" style={{ marginRight: "auto", gap: 6 }}>{hover && <><span className="sw" style={{ display: "inline-block", width: 22, height: 22, borderRadius: 5, background: hover, border: "1px solid #0002" }} /><code>{hover}</code></>}</span><button className="btn" onClick={onClose}>Fechar</button></>}>
      {!images.length ? <div className="muted">Envie a logo primeiro.</div> : (
        <div className="stack gap-12">
          {images.map((u, i) => (
            <img key={i} src={u} alt="" className="pick-img"
              onMouseMove={(e) => { const [x, y] = pos(e); void colorAt(u, x, y).then(setHover); }}
              onClick={(e) => { const [x, y] = pos(e); void colorAt(u, x, y).then((c) => c && onPick(c)); }} />
          ))}
          <div className="muted small">Passe o mouse para ver a cor; clique para usar.</div>
        </div>
      )}
    </Modal>
  );
}
